import React from 'react';
import { Box, Button, IconButton } from '@mui/material';
import { VolumeUp as SpeakerIcon } from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import UnitPageShell from '../../components/shared/UnitPageShell';
import ZoomableImage from '../../components/shared/ZoomableImage';
import EditableTextBlock from '../../components/shared/EditableTextBlock';
import { useAuth } from '../../context/AuthContext';
import { useTTS } from '../../hooks/useTTS';
import { uploadUnitAsset, updateUnitContent, useUnitOverride } from '../../services/unitOverridesService';

const PAGE_ID = 'm6_u5_x1_aktive_fuehler';
const IMAGE_BASE = '/assets/drawable/';

type TextBlock = { heading: string; body: string };

const textBlocks: TextBlock[] = [
  {
    heading: '',
    body: 'Aktive Fühler sind Sensoren, die eine externe Spannungsversorgung (meist 24V) benötigen. Ihre integrierte Elektronik wandelt physikalische Messgrößen wie Temperatur, relative Feuchte, CO2- oder VOC-Konzentration in ein standardisiertes elektrisches Signal um – typischerweise ein 0-10V DC Spannungssignal oder digitale Daten für Bussysteme.'
  },
  {
    heading: 'Elektrischer Anschluss im Detail',
    body: ''
  },
  {
    heading: 'Anschluss eines 0-10V DC Fühlers',
    body: `Ein aktiver Fühler mit 0-10V Ausgang wird typischerweise wie folgt angeschlossen, was auch im entsprechenden Ausschnitt eines Schaltplans ersichtlich wird:

•   **Spannungsversorgung (z.B. 24V):** Die Klemme 'G' (oder V+) wird mit der positiven Spannung und 'G0' (oder M/GND) mit der Systemmasse verbunden.
•   **Signalausgang (0-10V DC):** Die Klemme 'U' (oder Y/Out) führt das Spannungssignal und wird an einen Analogeingang (AI) der DDC angeschlossen. Die zugehörige Signalmasse 'M' (oder AGND) des Fühlers wird ebenfalls mit dem AI der DDC (dessen Masseanschluss) verbunden.

Die genauen Klemmenbezeichnungen sind stets dem Datenblatt des Fühlers zu entnehmen.`
  },
  {
    heading: 'Anschluss eines Bus-Fühlers (z.B. Modbus RTU)',
    body: `Bus-fähige Fühler (z.B. für Modbus RTU über RS485) benötigen ebenfalls eine Spannungsversorgung (G, G0). Die Datenkommunikation erfolgt über separate Klemmen:

•   **Datenleitungen:** 'A' (D+) und 'B' (D-) für das differentielle RS485-Signal.
•   **Bus-Schirm (optional):** Eine 'GND' oder 'SHLD' Klemme für den Kabelschirm.

Wie in einem Schaltplan für Bussysteme dargestellt, werden die Fühler in Linie (Daisy-Chain) verdrahtet, und jeder Fühler muss eine eindeutige Adresse auf dem Bus besitzen.`
  },
  {
    heading: 'Skalierung: Das A&O bei 0-10V Fühlern',
    body: `Der Analogeingang (AI) einer DDC liest nur eine Spannung (z.B. 3,5 Volt). Um diesen Wert in eine aussagekräftige physikalische Größe (z.B. 22°C oder 700 ppm CO2) umzuwandeln, muss die DDC den Messbereich des Fühlers kennen. Diese 'Übersetzung' von Spannung in Messwert nennt man Skalierung. Die dafür notwendigen Informationen stehen im Datenblatt des Fühlers.`
  },
  {
    heading: '',
    body: `**Typische Skalierungswerte (Beispiele aus Datenblättern):**
•   Temperaturfühler (Bereich -30°C bis +70°C): **0V = -30°C** und **10V = +70°C**
•   Feuchtefühler (Bereich 0% bis 100% r.F.): **0V = 0 % r.F.** und **10V = 100 % r.F.**
•   CO2-Fühler (Bereich 0 ppm bis 2000 ppm): **0V = 0 ppm** und **10V = 2000 ppm**

Diese Wertepaare (unterer Messbereichswert bei 0V, oberer Messbereichswert bei 10V) werden in der DDC-Software für den jeweiligen Analogeingang hinterlegt. Eine grafische Darstellung der Eingabemaske in der DDC-Software verdeutlicht diesen Prozess.`
  },
  {
    heading: 'Warum ist die korrekte Skalierung so kritisch?',
    body: `Eine exakte Skalierung ist fundamental, denn:

•   **Genaue Istwerte:** Nur so erhält die DDC korrekte Informationen über den Zustand der Anlage.
•   **Funktionierende Regelung:** Alle HLK-Regelstrategien basieren auf diesen Istwerten. Eine falsche Skalierung führt zu ineffizientem Betrieb, Energieverschwendung und mangelndem Komfort.
•   **Zuverlässige Diagnose:** Fehlerhafte Messwerte machen eine sinnvolle Fehlersuche oder Optimierung unmöglich.

Bei Bus-Fühlern entfällt diese Art der Skalierung, da der Messwert direkt digital übertragen wird. Hier ist die korrekte Adressierung und das Auslesen der richtigen Datenpunkte entscheidend.`
  },
  {
    heading: '',
    body: 'Aktive Fühler für Temperatur, Feuchte, CO2 und VOC sind die Sinnesorgane der Gebäudeautomation. Ihre sorgfältige Auswahl, fachgerechte Installation, der korrekte elektrische Anschluss gemäß Schaltplan und insbesondere die präzise Skalierung der 0-10V Signale in der DDC sind unerlässlich für eine zuverlässige, effiziente und komfortable Regelung von HLK-Anlagen.'
  }
];

const images = [
  { id: 'img_fuehler_grid_1', label: 'Raumtemperaturfühler', src: `${IMAGE_BASE}zzz_help.png`, alt: 'Aktiver Raumtemperaturfühler mit 0-10V Ausgang' },
  { id: 'img_fuehler_grid_2', label: 'CO2-Kanalfühler', src: `${IMAGE_BASE}zzz_help.png`, alt: 'Aktiver CO2-Kanalfühler mit Modbus' },
  { id: 'img_fuehler_grid_3', label: 'Kombifühler Außen', src: `${IMAGE_BASE}zzz_help.png`, alt: 'Kombifühler Temperatur/Feuchte' },
  { id: 'img_fuehler_grid_4', label: 'VOC-Raumfühler', src: `${IMAGE_BASE}zzz_help.png`, alt: 'Aktiver VOC/Mischgas-Raumfühler' },
  { id: 'img_schaltplan_0_10v', label: 'Schaltplan 0-10V', src: `${IMAGE_BASE}zzz_help.png`, alt: 'Schaltplanausschnitt: Anschluss 0-10V Temperaturfühler' },
  { id: 'img_schaltplan_bus', label: 'Schaltplan Modbus', src: `${IMAGE_BASE}zzz_help.png`, alt: 'Schaltplanausschnitt: Anschluss Modbus CO2-Fühler' },
  { id: 'img_skalierung_ddc', label: 'DDC Skalierung', src: `${IMAGE_BASE}zzz_help.png`, alt: 'Skalierungseingabe in DDC-Software' }
];

const Page: React.FC = () => {
  const navigate = useNavigate();
  const { token, canEditTextsM6, canUploadImagesM6 } = useAuth();
  const canEditTexts = canEditTextsM6;
  const canUploadImages = canUploadImagesM6;

  const { override, refresh } = useUnitOverride(PAGE_ID);

  const mergedBlocks = React.useMemo(
    () => textBlocks.map((tb, i) => ({ ...tb, body: override?.texts?.[`block_${i}`] || tb.body })),
    [override]
  );

  const imgSrc = (key: string, fallback: string) => {
    const v = override?.images?.[key];
    if (!v) return fallback;
    const s = String(v);
    return s.startsWith('/assets/') || s.startsWith('assets/') ? s : `/assets/unit_custom/${s}`;
  };

  const fullPlainText = React.useMemo(() => {
    return mergedBlocks.map(b => `${b.heading ? b.heading + '. ' : ''}${b.body}`).join(' ');
  }, [mergedBlocks]);

  const { speak, stop, speaking } = useTTS({ text: fullPlainText });

  const handleImageReplace = React.useCallback(async (imageKey: string, file: File) => {
    if (!token) return alert('Kein Token – bitte einloggen.');
    try {
      await uploadUnitAsset(PAGE_ID, imageKey, file, token);
      alert('Bild erfolgreich aktualisiert.');
      await refresh();
    } catch (e: any) {
      alert('Upload fehlgeschlagen: ' + (e?.message || e));
    }
  }, [token, refresh]);

  const handleTextSave = React.useCallback(async (blockId: string, newBody: string) => {
    if (!token) return alert('Kein Token – bitte einloggen.');
    try {
      await updateUnitContent(PAGE_ID, { texts: { [blockId]: newBody } }, token);
      alert('Text gespeichert.');
      await refresh();
    } catch (e: any) {
      alert('Speichern fehlgeschlagen: ' + (e?.message || e));
    }
  }, [token, refresh]);

  const ttsButton = React.createElement(IconButton, {
    onClick: speaking ? stop : speak,
    'aria-label': 'Text vorlesen',
    sx: { ml: 1 }
  }, React.createElement(SpeakerIcon, { sx: { fontSize: 28 } }));

  return React.createElement(
    UnitPageShell,
    { title: '6.5.1 Aktive Fühler: Temperatur, Feuchte, CO2, VOC', actions: ttsButton },
    React.createElement(Box, { sx: { display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, gap: 3 } },
      // Bilder-Spalte
      React.createElement(Box, { sx: { display: 'flex', flexDirection: 'column', gap: 2 } },
        // 2x2 Grid für erste 4 Bilder
        React.createElement(Box, { sx: { display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 2 } },
          images.slice(0, 4).map(img =>
            React.createElement(ZoomableImage, {
              key: img.id,
              src: imgSrc(img.id, img.src),
              alt: img.alt,
              label: img.label,
              onReplace: canUploadImages ? (file: File) => handleImageReplace(img.id, file) : undefined
            })
          )
        ),
        // Weitere Bilder einzeln
        images.slice(4).map(img =>
          React.createElement(ZoomableImage, {
            key: img.id,
            src: imgSrc(img.id, img.src),
            alt: img.alt,
            label: img.label,
            onReplace: canUploadImages ? (file: File) => handleImageReplace(img.id, file) : undefined
          })
        )
      ),

      // Text-Spalte
      React.createElement(Box, { sx: { display: 'flex', flexDirection: 'column', gap: 2 } },
        mergedBlocks.map((block, i) =>
          React.createElement(EditableTextBlock, {
            key: i,
            pageId: PAGE_ID,
            blockId: `block_${i}`,
            heading: block.heading,
            body: block.body,
            onSave: (newBody: string) => handleTextSave(`block_${i}`, newBody)
          })
        ),

        React.createElement(Box, { sx: { display: 'flex', gap: 1, mt: 2 } },
          React.createElement(Button, {
            variant: 'outlined',
            onClick: () => navigate('/units/m6_u5_x99_quiz')
          }, 'Zum Quiz')
        )
      )
    )
  );
};

export default Page;


